import { ScenarioValidationError } from '../errors.js'
import type { ScenarioConfig } from './types.js'

export interface ValidationResult {
  valid: boolean
  warnings: string[]
  errors: string[]
}

export function validateScenario(config: ScenarioConfig): ValidationResult {
  const warnings: string[] = []
  const errors: string[] = []

  if (!config.briefing.trim()) {
    errors.push('Briefing cannot be empty')
  }

  if (!config.ai_rules.role.trim()) {
    errors.push('ai_rules.role cannot be empty')
  }

  if (!config.repo.trim()) {
    errors.push('repo cannot be empty')
  }

  if (!config.commit.trim()) {
    errors.push('commit cannot be empty — pin to a specific commit SHA for reproducibility')
  } else if (!/^[0-9a-f]{7,40}$/i.test(config.commit.trim())) {
    errors.push(
      'commit must be a hex SHA (7-40 characters) — branch/tag names are not allowed for reproducibility',
    )
  }

  if (config.ai_rules.rules.length === 0) {
    warnings.push('ai_rules.rules is empty — the AI will have no behavioral constraints')
  }

  if (config.type === 'debug') {
    if (config.patch.length === 0) {
      warnings.push('debug scenario has no patches — a bug must be injected via patch')
    }
    if (!config.solution?.trim()) {
      warnings.push('solution is empty — interviewers will have no solution reference')
    }
  }

  if (config.type === 'feature') {
    const hasCriteria =
      (config.acceptance_criteria && config.acceptance_criteria.length > 0) ||
      (config.evaluation && config.evaluation.criteria && config.evaluation.criteria.length > 0)
    if (!hasCriteria) {
      warnings.push(
        'feature scenario has no acceptance_criteria or evaluation.criteria — candidates need a definition of done',
      )
    }
  }

  if (!config.evaluation) {
    warnings.push('No evaluation criteria defined')
  }

  if (!config.interviewer_guide) {
    warnings.push(
      'No interviewer_guide defined — interviewers will have limited context during the session',
    )
  }

  return {
    valid: errors.length === 0,
    warnings,
    errors,
  }
}

export function validateScenarioOrThrow(config: ScenarioConfig): ValidationResult {
  const result = validateScenario(config)
  if (!result.valid) {
    throw new ScenarioValidationError('scenario validation failed', result.errors)
  }
  return result
}
